import { create } from 'zustand';
import { MasterPatient } from '@/types';
import { supabase } from '@/lib/supabase';
import { trackChange } from '@/lib/utils/sync-tracker';

interface MasterPatientsState {
  masterPatients: MasterPatient[];
  isLoading: boolean;
  error: string | null;
  lastUpdated: number;
  pendingUpdates: Set<string>;
  
  // Core operations
  fetchMasterPatients: () => Promise<void>;
  getMasterPatient: (id: string) => MasterPatient | undefined;
  getMasterPatientByContact: (contactNumber: string) => MasterPatient | undefined;
  
  // CRUD operations
  createMasterPatient: (patient: {
    name: string;
    age: string;
    gender: 'male' | 'female' | 'other';
    contactNumber: string;
    location?: string;
    referralDoctor?: string;
    emergencyContact?: string;
    bloodGroup?: string;
    allergies?: string;
    medicalHistory?: string;
  }) => Promise<string>; // Returns master patient ID
  
  updateMasterPatient: (id: string, updates: Partial<MasterPatient>) => Promise<void>;
  deleteMasterPatient: (id: string) => Promise<void>;
  
  // Search operations
  searchMasterPatients: (query: string) => MasterPatient[];
  
  // Utility functions
  findOrCreateMasterPatient: (patient: {
    name: string;
    age: string;
    gender: 'male' | 'female' | 'other';
    contactNumber: string;
    location?: string;
    referralDoctor?: string;
  }) => Promise<string>; // Returns master patient ID
}

// Cache functions
const loadMasterPatientsCache = (): MasterPatient[] => {
  try {
    const cached = localStorage.getItem('masterPatientsCache');
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      // Cache valid for 5 minutes
      if (Date.now() - timestamp < 5 * 60 * 1000) {
        return data;
      }
    }
  } catch (error) {
    console.warn('Failed to load master patients cache:', error);
  }
  return [];
};

const saveMasterPatientsCache = (patients: MasterPatient[]) => {
  try {
    localStorage.setItem('masterPatientsCache', JSON.stringify({
      data: patients,
      timestamp: Date.now()
    }));
  } catch (error) {
    console.warn('Failed to save master patients cache:', error);
  }
};

export const useMasterPatientsStore = create<MasterPatientsState>()((set, get) => ({
  masterPatients: loadMasterPatientsCache(),
  isLoading: false,
  error: null,
  lastUpdated: 0,
  pendingUpdates: new Set(),

  fetchMasterPatients: async () => {
    set({ isLoading: true, error: null });
    try {
      console.log('Fetching master patients...');
      const { data, error } = await supabase
        .from('master_patients')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Supabase error fetching master patients:', error);
        throw error;
      }

      // Map snake_case to camelCase
      const mappedPatients = (data || []).map(patient => ({
        id: patient.id,
        name: patient.name,
        age: patient.age,
        gender: patient.gender,
        contactNumber: patient.contact_number,
        location: patient.location,
        referralDoctor: patient.referral_doctor,
        emergencyContact: patient.emergency_contact,
        bloodGroup: patient.blood_group,
        allergies: patient.allergies,
        medicalHistory: patient.medical_history,
        createdAt: patient.created_at,
        updatedAt: patient.updated_at
      }));

      console.log('Fetched master patients:', mappedPatients);
      set({ masterPatients: mappedPatients, isLoading: false, lastUpdated: Date.now() });
      saveMasterPatientsCache(mappedPatients);
    } catch (error) {
      console.error('Error fetching master patients:', error);
      set({ error: (error as Error).message, isLoading: false });
    }
  },

  getMasterPatient: (id: string) => {
    return get().masterPatients.find(p => p.id === id);
  },

  getMasterPatientByContact: (contactNumber: string) => {
    return get().masterPatients.find(p => p.contactNumber === contactNumber);
  },

  createMasterPatient: async (patient) => {
    try {
      console.log('=== START: Creating master patient ===');
      console.log('Patient data:', patient);
      
      // First, try to find existing master patient by contact number
      console.log('Checking for existing patient with contact:', patient.contactNumber);
      const existingPatient = get().getMasterPatientByContact(patient.contactNumber);
      if (existingPatient) {
        console.log('✅ Found existing master patient:', existingPatient.id);
        return existingPatient.id;
      }
      
      console.log('❌ No existing patient found, proceeding with creation...');
      
      // Use direct database operations instead of RPC function
      console.log('🔄 Creating master patient via direct database operations');
      
      const { data, error } = await supabase
        .from('master_patients')
        .insert([{
          name: patient.name,
          age: patient.age,
          gender: patient.gender,
          contact_number: patient.contactNumber,
          location: patient.location,
          referral_doctor: patient.referralDoctor,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }])
        .select('id')
        .single();

      if (error) {
        console.error('❌ Master patient creation failed:', error);
        throw error;
      }

      console.log('✅ Master patient created successfully:', data);
      
      // Refresh the master patients list
      console.log('🔄 Refreshing master patients list...');
      await get().fetchMasterPatients();
      
      console.log('✅ Successfully created master patient:', data.id);
      console.log('=== END: Master patient creation successful ===');
      return data.id;
    } catch (error) {
      console.error('❌ FATAL ERROR in createMasterPatient:', error);
      console.error('Error details:', JSON.stringify(error, null, 2));
      set({ error: (error as Error).message });
      console.log('=== END: Function failed ===');
      throw error;
    }
  },

  updateMasterPatient: async (id, updates) => {
    try {
      const currentPatient = get().masterPatients.find(p => p.id === id);
      if (!currentPatient) throw new Error('Master patient not found');

      // Create optimistic update
      const optimisticPatient: MasterPatient = {
        ...currentPatient,
        ...updates,
        updatedAt: new Date().toISOString()
      };

      // Optimistic update
      set(state => {
        const updated = state.masterPatients.map(p => p.id === id ? optimisticPatient : p);
        saveMasterPatientsCache(updated);
        return { 
          masterPatients: updated, 
          pendingUpdates: new Set([...state.pendingUpdates, id])
        };
      });

      // Track change for incremental sync
      trackChange({
        id,
        table: 'master_patients',
        operation: 'update',
        data: {
          name: updates.name,
          age: updates.age,
          gender: updates.gender,
          contact_number: updates.contactNumber,
          location: updates.location,
          referral_doctor: updates.referralDoctor,
          emergency_contact: updates.emergencyContact,
          blood_group: updates.bloodGroup,
          allergies: updates.allergies,
          medical_history: updates.medicalHistory,
          updated_at: optimisticPatient.updatedAt
        },
        originalData: {
          name: currentPatient.name,
          age: currentPatient.age,
          gender: currentPatient.gender,
          contact_number: currentPatient.contactNumber,
          location: currentPatient.location,
          referral_doctor: currentPatient.referralDoctor,
          emergency_contact: currentPatient.emergencyContact,
          blood_group: currentPatient.bloodGroup,
          allergies: currentPatient.allergies,
          medical_history: currentPatient.medicalHistory,
          updated_at: currentPatient.updatedAt
        }
      });

      // Backend sync
      const { data, error } = await supabase
        .from('master_patients')
        .update({
          name: updates.name,
          age: updates.age,
          gender: updates.gender,
          contact_number: updates.contactNumber,
          location: updates.location,
          referral_doctor: updates.referralDoctor,
          emergency_contact: updates.emergencyContact,
          blood_group: updates.bloodGroup,
          allergies: updates.allergies,
          medical_history: updates.medicalHistory,
          updated_at: optimisticPatient.updatedAt
        })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating master patient:', error);
        // Rollback optimistic update
        set(state => {
          const updated = state.masterPatients.map(p => p.id === id ? currentPatient : p);
          saveMasterPatientsCache(updated);
          return { 
            masterPatients: updated, 
            pendingUpdates: new Set([...state.pendingUpdates].filter(pid => pid !== id)),
            error: error.message
          };
        });
        throw error;
      }

      // Update with server data
      const mappedPatient = {
        id: data.id,
        name: data.name,
        age: data.age,
        gender: data.gender,
        contactNumber: data.contact_number,
        location: data.location,
        referralDoctor: data.referral_doctor,
        emergencyContact: data.emergency_contact,
        bloodGroup: data.blood_group,
        allergies: data.allergies,
        medicalHistory: data.medical_history,
        createdAt: data.created_at,
        updatedAt: data.updated_at
      };

      set(state => {
        const updated = state.masterPatients.map(p => p.id === id ? mappedPatient : p);
        saveMasterPatientsCache(updated);
        return { 
          masterPatients: updated, 
          pendingUpdates: new Set([...state.pendingUpdates].filter(pid => pid !== id))
        };
      });
    } catch (error) {
      console.error('Error updating master patient:', error);
      set({ error: (error as Error).message });
    }
  },

  deleteMasterPatient: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      const { error } = await supabase
        .from('master_patients')
        .delete()
        .eq('id', id);

      if (error) throw error;

      set(state => {
        const updated = state.masterPatients.filter(p => p.id !== id);
        saveMasterPatientsCache(updated);
        return { masterPatients: updated, isLoading: false };
      });
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false });
    }
  },

  searchMasterPatients: (query: string) => {
    const patients = get().masterPatients;
    if (!query.trim()) return patients;

    const searchTerm = query.toLowerCase().trim();
    return patients.filter(patient => 
      patient.name.toLowerCase().includes(searchTerm) ||
      patient.contactNumber.includes(searchTerm)
    );
  },

  findOrCreateMasterPatient: async (patient) => {
    try {
      // First try to find existing master patient by contact number
      const existingPatient = get().getMasterPatientByContact(patient.contactNumber);
      if (existingPatient) {
        console.log('Found existing master patient:', existingPatient.id);
        return existingPatient.id;
      }

      // If not found, create new master patient
      console.log('Creating new master patient for:', patient.name);
      return await get().createMasterPatient(patient);
    } catch (error) {
      console.error('Error in findOrCreateMasterPatient:', error);
      throw error;
    }
  }
})); 